import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CheckCircle2, CreditCard } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useCart } from "@/context/CartContext";

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader, Money } from "@/components/common";
import { toast } from "sonner";

export default function Checkout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { clearCart } = useCart();
  const items = location.state?.items || [];
  const [coupon, setCoupon] = useState("");
  const [address, setAddress] = useState({ name: "", phone: "", line: "", city: "", pincode: "" });
  const [placing, setPlacing] = useState(false);
  const [done, setDone] = useState(null);

  const hasProduct = items.some((i) => i.type === "product");
  const total = items.reduce((s, i) => s + i.price * (i.qty || 1), 0);

  const placeOrder = async () => {
    setPlacing(true);
    try {
      const { data } = await api.post("/payments/create-order", {
        items: items.map((i) => ({ type: i.type, id: i.id, qty: i.qty || 1 })), coupon, address,
      });
      if (data.free) { clearCart(); setDone(data.order); toast.success("Enrolled successfully"); setPlacing(false); return; }
      const ok = await loadRazorpay();
      if (!ok || !window.Razorpay) { toast.error("Could not load payment gateway"); setPlacing(false); return; }
      const rzp = new window.Razorpay({
        key: data.key_id, amount: data.amount, currency: data.currency,
        name: "VIRIKS EdTech", description: "Course / Product purchase", order_id: data.razorpay_order_id,
        prefill: { name: address.name || "", contact: address.phone || "" },
        theme: { color: "#ED1C24" },
        handler: async (resp) => {
          try {
            const { data: order } = await api.post("/payments/verify", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            clearCart(); setDone(order); toast.success("Payment successful");
          } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Payment verification failed");
          } finally { setPlacing(false); }
        },
        modal: { ondismiss: () => setPlacing(false) },
      });
      rzp.on("payment.failed", () => { toast.error("Payment failed. Please try again."); setPlacing(false); });
      rzp.open();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setPlacing(false);
    }
  };

  if (!items.length) { navigate("/courses"); return null; }

  if (done)
    return (
      <div className="max-w-lg mx-auto px-6 py-20 text-center min-h-[60vh]" data-testid="order-success">
        <CheckCircle2 size={64} className="text-emerald-500 mx-auto" />
        <h1 className="font-heading text-3xl font-bold text-navy mt-4">Order Confirmed!</h1>
        <p className="text-muted-foreground mt-2">Order #{done.order_no} — <Money amount={done.total} /></p>
        <div className="flex gap-3 justify-center mt-8">
          <Button className="viriks-navy text-white" onClick={() => navigate("/learner")}>Go to Dashboard</Button>
          <Button variant="outline" onClick={() => navigate("/learner/orders")}>View Orders</Button>
        </div>
      </div>
    );

  return (
    <div className="max-w-5xl mx-auto px-6 py-10 min-h-[60vh]" data-testid="checkout-page">
      <PageHeader title="Checkout" />
      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-sm text-emerald-800 flex items-center gap-2">
            <CreditCard size={16} /> <span><strong>Secure Payment:</strong> Your payment is processed securely via Razorpay. UPI, cards, netbanking & wallets supported.</span>
          </div>
          {hasProduct && (
            <div className="bg-white border border-border rounded-lg p-6">
              <h3 className="font-heading font-bold text-navy mb-4">Shipping Address</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label>Full Name</Label><Input value={address.name} onChange={(e) => setAddress({ ...address, name: e.target.value })} data-testid="addr-name" /></div>
                <div><Label>Phone</Label><Input value={address.phone} onChange={(e) => setAddress({ ...address, phone: e.target.value })} /></div>
                <div className="sm:col-span-2"><Label>Address</Label><Input value={address.line} onChange={(e) => setAddress({ ...address, line: e.target.value })} /></div>
                <div><Label>City</Label><Input value={address.city} onChange={(e) => setAddress({ ...address, city: e.target.value })} /></div>
                <div><Label>Pincode</Label><Input value={address.pincode} onChange={(e) => setAddress({ ...address, pincode: e.target.value })} /></div>
              </div>
            </div>
          )}
          <div className="bg-white border border-border rounded-lg p-6">
            <h3 className="font-heading font-bold text-navy mb-4">Items</h3>
            {items.map((it) => (
              <div key={`${it.type}-${it.id}`} className="flex justify-between py-2 text-sm border-b border-border last:border-0">
                <span>{it.title} {it.qty > 1 && `× ${it.qty}`}</span><Money amount={it.price * (it.qty || 1)} />
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white border border-border rounded-lg p-6 h-fit">
          <h3 className="font-heading font-bold text-navy mb-4">Summary</h3>
          <div className="flex gap-2 mb-4">
            <Input placeholder="Coupon code" value={coupon} onChange={(e) => setCoupon(e.target.value)} data-testid="coupon-input" />
          </div>
          <div className="flex justify-between font-heading font-black text-lg text-navy border-t border-border pt-3"><span>Total</span><Money amount={total} /></div>
          <Button className="w-full mt-5 viriks-amber text-white font-semibold" onClick={placeOrder} disabled={placing} data-testid="pay-btn">
            <CreditCard size={16} className="mr-2" /> {placing ? "Processing..." : "Pay Now"}
          </Button>
        </div>
      </div>
    </div>
  );
}
